/**
 * DebugReportBugRefPanel — post-submit bug-reference display + copy
 * affordance.
 *
 * Extracted from DebugReport.tsx (2026-05-25) so that file stays under the
 * 300-LOC ceiling. The bug-ref display + clipboard-copy concern is
 * self-contained: it owns its own copy state + handlers and renders only
 * when a `bugRef` is present.
 *
 * WHY the execCommand fallback (D-33 R2 — no silent failures):
 * We prefer the async Clipboard API (Chrome ≥ 66, Firefox ≥ 63, Safari ≥
 * 13.1), but in restricted contexts — sandboxed iframes,
 * Permissions-Policy='clipboard-write=()', some PWA install paths,
 * non-secure contexts — `navigator.clipboard` can be present yet
 * `writeText` rejects. So we ALWAYS fall back to the <textarea> +
 * execCommand('copy') path on rejection, not only when the API is missing.
 * Only if BOTH paths fail do we surface an inline hint (rather than
 * silently dropping the affordance, and rather than flipping the dialog
 * away from the `sent` state — the ref must stay visible so the user can
 * read it off-screen).
 */

import { useCallback, useState } from 'react'
import { Button } from '@appic/ui-components'
import { useLanguage } from '@/contexts/LanguageContext'
import { Copy, Check } from 'lucide-react'
import type { ReportKind } from './debugReportCapture.types'

function execCommandCopy(ref: string): boolean {
  const ta = document.createElement('textarea')
  ta.value = ref
  ta.setAttribute('readonly', '')
  ta.style.position = 'fixed'
  ta.style.opacity = '0'
  document.body.appendChild(ta)
  ta.select()
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  document.body.removeChild(ta)
  return ok
}

interface DebugReportBugRefPanelProps {
  bugRef: string
  /** Report kind — selects the ref label/hint copy. Defaults to `'bug'`. */
  kind?: ReportKind
}

export function DebugReportBugRefPanel({ bugRef, kind = 'bug' }: DebugReportBugRefPanelProps) {
  const { t } = useLanguage()
  const isFeature = kind === 'feature'
  // Kind-scoped i18n with fallback to the shared bug key (see DebugReport.tsx).
  const tk = (suffix: string): string => {
    if (isFeature) {
      const feat = t(`debug.report.feature.${suffix}`)
      if (feat !== `debug.report.feature.${suffix}`) return feat
    }
    return t(`debug.report.${suffix}`)
  }
  // Transient "Copied!" indicator on the copy button. Cleared after the
  // timer fires.
  const [refCopied, setRefCopied] = useState(false)
  // Inline copy-failed hint shown when BOTH the async Clipboard API and
  // the execCommand fallback fail.
  const [refCopyFailed, setRefCopyFailed] = useState(false)

  const handleCopyRef = useCallback(async (ref: string) => {
    let copied = false
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(ref)
        copied = true
      } catch {
        // Async Clipboard API rejected — fall through to execCommand path.
      }
    }
    if (!copied) {
      copied = execCommandCopy(ref)
    }
    if (copied) {
      setRefCopyFailed(false)
      setRefCopied(true)
      window.setTimeout(() => setRefCopied(false), 2000)
    } else {
      setRefCopyFailed(true)
    }
  }, [])

  return (
    <div className="w-full max-w-sm rounded-lg border border-border bg-muted/30 p-3 text-center">
      <p className="text-xs font-medium text-muted-foreground mb-1.5">
        {tk('refLabel')}
      </p>
      <div className="flex items-center justify-center gap-2">
        <code
          className="rounded bg-background px-2 py-1 font-mono text-sm font-semibold tracking-wider"
          data-testid="bug-ref-value"
        >
          {bugRef}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => handleCopyRef(bugRef)}
          aria-label={t('debug.report.refCopy')}
          className="gap-1.5"
          data-testid="bug-ref-copy"
        >
          {refCopied ? (
            <>
              <Check className="h-3.5 w-3.5" />
              {t('debug.report.refCopied')}
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              {t('debug.report.refCopy')}
            </>
          )}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        {tk('refHint')}
      </p>
      {refCopyFailed && (
        <p
          className="text-xs text-warn-foreground mt-1.5"
          role="alert"
          data-testid="bug-ref-copy-failed"
        >
          {t('debug.report.refCopyFailed')}
        </p>
      )}
    </div>
  )
}
